import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Mic, Square, X, Send, Loader2, Volume2, Check } from "lucide-react";
import DictateButton from "@/components/DictateButton";

const LANG_LABEL = {
  "en-IN": "English", "hi-IN": "हिन्दी", "ta-IN": "தமிழ்", "te-IN": "తెలుగు",
  "kn-IN": "ಕನ್ನಡ", "mr-IN": "मराठी", "bn-IN": "বাংলা", "ml-IN": "മലയാളം",
  "gu-IN": "ગુજરાતી", "pa-IN": "ਪੰਜਾਬੀ", "od-IN": "ଓଡ଼ିଆ",
};

const GREETING =
  "Namaste — I'm your Chef Hire concierge. Tap the mic and ask me about private chefs, cities, pricing, or our mini-catering menus. You can speak in English or any Indian language.";

export const VoiceConcierge = () => {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [sessionId, setSessionId] = useState(null);
  const [lastLang, setLastLang] = useState("en-IN");
  const [textInput, setTextInput] = useState("");
  const [error, setError] = useState("");
  const [showCallback, setShowCallback] = useState(false);
  const [cbName, setCbName] = useState("");
  const [cbPhone, setCbPhone] = useState("");
  const [cbLoading, setCbLoading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const greetingB64Ref = useRef(null);
  const greetedRef = useRef(false);
  const openRef = useRef(false);
  const dismissedRef = useRef(false);

  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Fetch greeting audio + proactively greet a few seconds after landing
  useEffect(() => {
    let timer;
    (async () => {
      try {
        const { data } = await api.get("/voice/greeting", { params: { lang: "en-IN" } });
        greetingB64Ref.current = data.audio || null;
        if (data.text) setMessages((m) => [{ role: "assistant", text: data.text }, ...m.slice(1)]);
      } catch { /* silent */ }
    })();
    timer = setTimeout(() => {
      if (openRef.current || dismissedRef.current || greetedRef.current) return;
      // Don't cover a form the guest is filling in (Diwali enquiry, booking pages) on small screens
      if (window.innerWidth < 1024) return;
      greetedRef.current = true;
      setOpen(true);
      playGreeting();
    }, 4500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const playB64 = (b64) => {
    if (!b64) return;
    try {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(`data:audio/wav;base64,${b64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      audio.play().catch(() => setSpeaking(false));
    } catch {
      setSpeaking(false);
    }
  };

  // Greeting with autoplay-block fallback (plays on first interaction if blocked)
  const playGreeting = () => {
    const b64 = greetingB64Ref.current;
    if (!b64) return;
    const audio = new Audio(`data:audio/wav;base64,${b64}`);
    audioRef.current = audio;
    setSpeaking(true);
    audio.onended = () => setSpeaking(false);
    audio.onerror = () => setSpeaking(false);
    audio.play().catch(() => {
      setSpeaking(false);
      const handler = () => { window.removeEventListener("pointerdown", handler); playGreeting(); };
      window.addEventListener("pointerdown", handler, { once: true });
    });
  };

  const sendChat = async (message, langCode) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/voice/chat", {
        session_id: sessionId,
        message,
        language_code: langCode || lastLang,
        speak: true,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.lead_created && data.lead) {
        setMessages((m) => [
          ...m,
          { role: "system", text: `Shared with our team — ${data.lead.name}, ${data.lead.phone}${data.lead.city ? " · " + data.lead.city : ""}` },
        ]);
      }
      playB64(data.audio);
    } catch (e) {
      setError("The concierge is unavailable right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAudio = async (blob) => {
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", blob, "recording.webm");
      fd.append("language_code", "unknown");
      const { data } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = (data.transcript || "").trim();
      if (!transcript) {
        setError("I didn't catch that — please try again.");
        setLoading(false);
        return;
      }
      const lang = data.language_code || "en-IN";
      setLastLang(lang);
      setMessages((m) => [...m, { role: "user", text: transcript }]);
      await sendChat(transcript, lang);
    } catch (e) {
      setError("Couldn't process the recording. Check mic permissions and try again.");
      setLoading(false);
    }
  };

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        stopStream();
        if (blob.size > 0) handleAudio(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setError("Microphone access is blocked. Enable it in your browser settings.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const submitText = (e) => {
    e.preventDefault();
    const t = textInput.trim();
    if (!t || loading) return;
    setTextInput("");
    setMessages((m) => [...m, { role: "user", text: t }]);
    sendChat(t, lastLang);
  };

  const closePanel = () => {
    stopRecording();
    if (audioRef.current) audioRef.current.pause();
    setSpeaking(false);
    dismissedRef.current = true;
    setOpen(false);
  };

  const toggleMic = () => (recording ? stopRecording() : startRecording());

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!cbName.trim() || cbPhone.replace(/\D/g, "").length < 10) {
      setError("Please enter your name and a valid phone number.");
      return;
    }
    setCbLoading(true);
    setError("");
    try {
      await api.post("/leads/callback", { name: cbName, phone: cbPhone });
      setMessages((m) => [...m, { role: "system", text: `Callback requested — we'll ring you on ${cbPhone}` }]);
      setShowCallback(false);
      setCbName("");
      setCbPhone("");
    } catch (err) {
      setError("Couldn't submit. Please try again.");
    } finally {
      setCbLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="voice-concierge-fab"
          aria-label="Open voice concierge"
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full border border-[#C9962F]/40 bg-[#141310]/90 backdrop-blur-xl shadow-[0_8px_40px_rgba(201,150,47,0.25)] hover:border-[#C9962F] transition-colors"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#E0B75B] to-[#B07F27]">
            <Mic size={16} className="text-black" />
            <span className="absolute inset-0 rounded-full ring-2 ring-[#C9962F]/40 animate-ping" />
          </span>
          <span className="small-caps text-[11px] tracking-wide text-[#E7D8B5]">Ask Concierge</span>
        </button>
      )}

      {open && (
        <div
          data-testid="voice-concierge-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] flex flex-col rounded-2xl overflow-hidden border border-[#C9962F]/30 bg-[#0E0D0B]/95 backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#171512] to-[#0E0D0B]">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#E0B75B] to-[#B07F27]">
                {speaking ? <Volume2 size={15} className="text-black" /> : <Mic size={15} className="text-black" />}
              </span>
              <div className="leading-tight">
                <div className="font-script text-lg text-white -mb-0.5">Concierge</div>
                <div className="small-caps text-[9px] text-[#C9962F]">
                  {speaking ? "Speaking…" : "Chef Hire · Voice"}
                </div>
              </div>
            </div>
            <button
              onClick={closePanel}
              data-testid="voice-close-btn"
              className="text-neutral-400 hover:text-white transition-colors"
              aria-label="Close concierge"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} data-testid="voice-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) =>
              m.role === "system" ? (
                <div key={i} className="flex justify-center">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C9962F]/10 border border-[#C9962F]/30 text-[11px] text-[#E7D8B5]">
                    <Check size={12} className="text-[#C9962F]" /> {m.text}
                  </div>
                </div>
              ) : (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#C9962F]/15 border border-[#C9962F]/30 text-[#F1E4C6] rounded-br-sm"
                        : "bg-white/[0.04] border border-white/10 text-neutral-200 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              )
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-white/[0.04] border border-white/10">
                  <Loader2 size={16} className="animate-spin text-[#C9962F]" />
                </div>
              </div>
            )}
            {error && <div className="text-center text-[12px] text-red-400/90 px-4">{error}</div>}
          </div>

          <div className="px-4 py-4 border-t border-white/10 bg-[#100F0C]">
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={toggleMic}
                disabled={loading}
                data-testid="voice-mic-btn"
                className={`relative flex h-16 w-16 items-center justify-center rounded-full transition-all disabled:opacity-40 ${
                  recording
                    ? "bg-red-500/90 shadow-[0_0_30px_rgba(239,68,68,0.5)]"
                    : "bg-gradient-to-br from-[#E0B75B] to-[#B07F27] hover:scale-105"
                }`}
                aria-label={recording ? "Stop recording" : "Start recording"}
              >
                {recording ? <Square size={22} className="text-white" fill="currentColor" /> : <Mic size={24} className="text-black" />}
                {recording && <span className="absolute inset-0 rounded-full ring-4 ring-red-500/40 animate-ping" />}
              </button>
              <div className="small-caps text-[10px] text-neutral-500">
                {recording ? "Listening… tap to send" : `Tap to speak · ${LANG_LABEL[lastLang] || "English"}`}
              </div>
              <a href="tel:+917971413816" data-testid="concierge-call-link" className="small-caps text-[10px] text-[#C9962F] hover:text-[#E7D8B5] transition-colors">
                Prefer to call? +91 79714 13816
              </a>

              <button
                type="button"
                onClick={() => setShowCallback((v) => !v)}
                data-testid="concierge-callback-toggle"
                className="small-caps text-[10px] text-neutral-500 hover:text-white transition-colors"
              >
                {showCallback ? "Hide callback form" : "Request a callback"}
              </button>
              {showCallback && (
                <form onSubmit={submitCallback} className="w-full space-y-2 pt-1" data-testid="concierge-callback-form">
                  <input
                    value={cbName}
                    onChange={(e) => setCbName(e.target.value)}
                    data-testid="callback-name"
                    placeholder="Your name"
                    className="w-full bg-white/[0.04] border border-white/10 focus:border-[#C9962F]/50 outline-none rounded-full px-4 py-2 text-sm text-neutral-200 placeholder:text-neutral-600"
                  />
                  <input
                    value={cbPhone}
                    onChange={(e) => setCbPhone(e.target.value)}
                    data-testid="callback-phone"
                    placeholder="Phone number"
                    className="w-full bg-white/[0.04] border border-white/10 focus:border-[#C9962F]/50 outline-none rounded-full px-4 py-2 text-sm text-neutral-200 placeholder:text-neutral-600"
                  />
                  <button
                    type="submit"
                    disabled={cbLoading}
                    data-testid="callback-submit"
                    className="w-full rounded-full bg-gradient-to-r from-[#E0B75B] to-[#B07F27] text-black h-9 small-caps text-[10px] disabled:opacity-50"
                  >
                    {cbLoading ? "Sending…" : "Request callback"}
                  </button>
                </form>
              )}

              <form onSubmit={submitText} className="w-full flex items-center gap-2">
                <input
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  data-testid="voice-text-input"
                  placeholder="…or type your question"
                  className="flex-1 bg-white/[0.04] border border-white/10 focus:border-[#C9962F]/50 outline-none rounded-full px-4 py-2 text-sm text-neutral-200 placeholder:text-neutral-600"
                />
                <DictateButton
                  testId="voice-dictate-btn"
                  label=""
                  context="A guest asking Chef Hire about private chefs, catering menus, pricing or availability. Hindi and English mixed."
                  onText={(t) => setTextInput((p) => [p.trim(), t].filter(Boolean).join(" "))}
                />
                <button
                  type="submit"
                  disabled={loading || !textInput.trim()}
                  data-testid="voice-send-btn"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C9962F]/20 border border-[#C9962F]/40 text-[#E7D8B5] hover:bg-[#C9962F]/30 transition-colors disabled:opacity-40"
                  aria-label="Send message"
                >
                  <Send size={15} />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
